import { useContext, useEffect, useMemo, useRef, useState } from "react";
import Dialog from "./component/Dialog";
import FileList from "./component/FileList";
import LoginForm from "./component/LoginForm";
import Progress from "./component/Progress";
import PropertiesModal from "./component/PropertiesModal";
import Sidebar from "./component/Sidebar";
import Toolbar from "./component/Toolbar";
import { SpaceContext } from "./SpaceContext";
import { uploadFile, uploadFolder as uploadFolderHelper } from "./utils/file";
import message from "./utils/message";
import service from "./utils/request";
import "nprogress/nprogress.css";
import "./App.css";
import { resolvePermissions } from "./utils/permissions";
import { Tab, Tabs } from "./component/Tabs";
import Proflie from "./component/Proflie";
import MailGroupManagement from "./component/MailGroupManagement";
import Search from './component/Search';
import LibraryPage from "./component/Library/LibraryPage";
import {
	faFileLines,
	faUserGroup,
	faUsersLine,
	faCalendarDays,
	faBriefcase,
} from "@fortawesome/free-solid-svg-icons";

// spaces is a list of object { id:, caption:, class: }
// path is a list of object ( id:, name: ) where the first id in the first element is
// the space id.

function App() {
	const { coolApps, isLogin, setIsLogin, getFilteredSpaces, checkingAuth } = useContext(SpaceContext);

	const [spaces, setSpaces] = useState([]);
	const [path, setPath] = useState([]);
	const [loading, setLoading] = useState(false);
	const [fileList, setFileList] = useState([]);
	const [sortOrder, setSortOrder] = useState({ field: "Name", order: "asc" });
	const [selectedItems, setSelectedItems] = useState({});
	const [uploadList, setUploadList] = useState({});
	const [isPropertiesModalVisible, setIsPropertiesModalVisible] =
		useState(false);
	const [isExistModalVisible, setIsExistModalVisible] = useState(false);
	const [currentItem, setCurrentItem] = useState({});
	const [view, setView] = useState("list");
	const [currentFiles, setCurrentFiles] = useState([]);
	const [activeTab, setActiveTab] = useState(() => {
		return 'docs';
	});
	const [activeSection, setActiveSection] = useState("docs");
	const resolvedPermissions = useMemo(
		() => resolvePermissions(selectedItems),
		[selectedItems]
	);
	const [selectedChildrenTab, setSelectedChildrenTab] = useState('profile');

	const uploadListRef = useRef({});

	const extensions = useMemo(() => {
		const allExtensions = ["txt", "md"];
		for (const key in coolApps) {
			if (coolApps.hasOwnProperty(key) && coolApps[key].extensions) {
				allExtensions.push(...coolApps[key].extensions);
			}
		}
		return allExtensions;
	}, [coolApps]);

	// what is this, this is not very useful, more like a set of utilities
	const spaceInfo = useMemo(() => {
		const space_id = path[0]?.id || "";
		const folder_id = path.length > 1 ? path[path.length - 1]?.id : "";
		const current_id = path[path.length - 1]?.id;
		return { space_id, folder_id, current_id };
	}, [path]);

	function login(username, password) {
		service
			.post("/signin", { username, password })
			.then(async () => {
				const res = await service.get("/user/profile").catch(() => null);
				setIsLogin(true);
				const params = new URLSearchParams(window.location.search);
				const redirect = params.get('redirect');
				window.location.href = redirect || '/document';
			})
			.catch((error) => {
				message("error", error);
			});
	}

	function logout() {
		service.get("/signout").finally(() => {
			window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname + window.location.search);
			setIsLogin(false);
		});
	}

	async function getSpaces() {
		setLoading(true);
		try {
			const filteredSpaces = await getFilteredSpaces();
			setSpaces(filteredSpaces);
			sessionStorage.setItem("spaces", JSON.stringify(filteredSpaces));
			await switchSpace(filteredSpaces[0]);
		} catch (error) {
			message("error", error);
		}
		setLoading(false);
	}

	function sortItems(items) {
		const sortedItems = [...items];

		sortedItems.sort((a, b) => {
			if (a.is_folder && !b.is_folder) {
				return -1;
			}
			if (!a.is_folder && b.is_folder) {
				return 1;
			}
			let x = 0;
			if (sortOrder.field === "Name") {
				x = a.item_name.localeCompare(b.item_name);
			} else {
				x = a.modified_time.localeCompare(b.modified_time);
			}
			return sortOrder.order === "asc" ? x : -x;
		});
		return sortedItems;
	}

	// Helper function to generate random permissions
	function generatePermissions(item) {
		return {
			readable: true, // 100% chance of being readable
			writable: item.access_level >= 7, // true if the file or folder was created in the current user's pace
		};
	}

	async function fetchFileList(id) {
		if (id === "explorer" || id === 'library') {
			return [];
		}
		try {
			const data = await service.get("/filer/list/" + id);
			const sortedData = sortItems(data);

			// Generate random permissions for each fetched item temporarily
			return sortedData.map((item) => ({
				...item,
				permissions: generatePermissions(item),
			}));
		} catch (error) {
			message("error", error);
		}
	}

	async function fetchSpacceFileList(id, spaceClass) {
		if (id === "explorer" || id === 'search') {
			return [];
		}
		try {
			let data = [];
			if (spaceClass === 'user' || spaceClass === 'orphan' || spaceClass === 'share' || spaceClass === 'special') {
				data = await service.get("/filer/list/" + id);
			}
			const sortedData = sortItems(data);

			return sortedData.map((item) => ({
				...item,
				permissions: generatePermissions(item),
			}));
		} catch (error) {
			console.log('Error fetching space files:', error);
			return []; // Return back to empty arrays
		}
	}

	async function switchSpace(space) {
		try {
			if (space.id === 'library') {
				setPath([{ name: space.caption, id: space.id }]);
				setFileList([]);
				return;
			}
			let fileData = await fetchSpacceFileList(space.id, space.class);
			setPath([{ name: space.caption, id: space.id }]);
			setFileList(fileData || []);
		} catch (error) {
			message("error", error);
		}
	}

	async function getFileList(id) {
		console.log(spaceInfo);

		setLoading(true);
		try {
			const data = await fetchFileList(id);
			setFileList(data);
			setLoading(false);
		} catch (error) {
			message("error", error);
		}
	}

	function openFolder(card) {
		const { item_id, item_name } = card;
		getFileList(item_id);
		if (item_id === spaceInfo.space_id) {
			setPath([{ id: item_id, name: item_name }]);
		} else {
			setPath([...path, { id: item_id, name: item_name }]);
		}
	}

	async function upload(file, action = "", tagIds = []) {
		const fileKey =
			Math.random().toString(36).substring(2, 15) +
			Math.random().toString(36).substring(2, 15);
		setUploadList((prevUploadList) => ({
			...prevUploadList,
			[fileKey]: {
				value: "0%",
				status: "pending",
				name: file.name,
				loaded: 0,
				total: file.size,
			},
		}));

		message("loading", "Uploading now");
		try {
			const fileItem = await uploadFile(
				file,
				spaceInfo,
				(value, cancelToken, id, loaded, total) => {
					setUploadList((prevUploadList) => {
						const result = {
							...prevUploadList,
							[fileKey]: {
								value,
								status: "pending",
								name: file.name,
								cancelToken,
								id,
								loaded: loaded,
								total: total,
							},
						};
						uploadListRef.current = result;

						return result;
					});
				},
				action
			);

			const itemId = fileItem?.item_id;

			// Assign tags to the uploaded file
			if (tagIds.length > 0 && itemId) {
				try {
					await service.post("/api/topic/assign", {
						file_id: itemId,
						topic_ids: tagIds,
					});
				} catch (tagError) {
					console.error("Failed to assign tags:", tagError);
				}
			}

			if (fileItem && typeof fileItem === "object") {
				const itemWithPermissions = {
					...fileItem,
					item_size: file.size || 0,
					permissions: generatePermissions(fileItem),
				};
				setFileList((prevFileList) => {
					let existingIndex = prevFileList.findIndex(
						(item) => item.item_id === itemWithPermissions.item_id
					);
					if (existingIndex < 0 && action === "replace") {
						existingIndex = prevFileList.findIndex(
							(item) =>
								!item.is_folder &&
								item.item_name === itemWithPermissions.item_name &&
								(item.item_type || "") === (itemWithPermissions.item_type || "")
						);
					}
					let updatedList;
					if (existingIndex >= 0) {
						updatedList = [...prevFileList];
						updatedList[existingIndex] = itemWithPermissions;
					} else {
						updatedList = [...prevFileList, itemWithPermissions];
					}
					return sortItems(updatedList);
				});
			}

			message("success", "Upload successfully");
		} catch (error) {
			message("error", error);
		}
	}

	async function uploadFolderAction(files) {
		message("loading", "Uploading folder...");
		const totalFiles = files ? Array.from(files).length : 0;
		const idToIndexMap = {};
		let currentIndex = 0;

		try {
			await uploadFolderHelper(files, spaceInfo, (value, cancelToken, id, loaded, total) => {
				if (id && !idToIndexMap[id]) {
					currentIndex += 1;
					idToIndexMap[id] = currentIndex;
				}
				const fileIndex = idToIndexMap[id] || 1;
				const displayName = totalFiles > 1 ? `Uploading (${fileIndex}/${totalFiles})...` : "Uploading...";

				setUploadList((prevUploadList) => {
					const result = {
						...prevUploadList,
						[id]: {
							value,
							status: "pending",
							name: displayName,
							cancelToken,
							id,
							loaded,
							total,
						},
					};
					uploadListRef.current = result;
					return result;
				});
			});
			message("success", "Folder uploaded successfully!");
			if (getFileList && spaceInfo.current_id) {
				getFileList(spaceInfo.current_id);
			}
		} catch (err) {
			console.error("Folder upload error:", err);
			message("error", "Failed to upload folder");
		}
	}


	async function handleItemClick(card) {
		const { item_id } = card;

		let selectedItemsCopy = { ...selectedItems };
		if (item_id in selectedItems) {
			delete selectedItemsCopy[item_id];
		} else {
			selectedItemsCopy[item_id] = card;
		}
		setSelectedItems(selectedItemsCopy);
	}

	async function checkExists(file) {
		try {
			const { exists } = await service.post("/filer/check_file_existence", {
				folder_id: spaceInfo.folder_id || spaceInfo.space_id,
				file_name: file.name,
				file_type: ".",
			});
			if (exists) {
				setIsExistModalVisible(true);
				setCurrentFiles((prevFiles) => [...prevFiles, file]);
			}
			return exists;
		} catch (error) {
			return false;
		}
	}

	async function replaceFile(replace = true) {
		const filesSlice = [...currentFiles];
		filesSlice.shift();
		setCurrentFiles(filesSlice);

		if (filesSlice.length === 0) {
			setIsExistModalVisible(false);
		}

		if (replace) {
			await upload(currentFiles[0], "replace");
		} else {
			await upload(currentFiles[0]);
		}
	}

	async function continueUpload() {
		replaceFile(false);
	}

	async function handleFileDrop(e) {
		e.preventDefault();
		const files = e.dataTransfer?.files;
		if (files) {
			for (var i = 0; i < files.length; i++) {
				const result = await checkExists(files[i]);
				if (!result) {
					upload(files[i]);
				}
			}
		}
	}

	// redundant useEffect removed

	useEffect(() => {
		if (isLogin) {
			const sessionSpaces = sessionStorage.getItem("spaces");
			const sessionPath = sessionStorage.getItem("path");

			if (sessionSpaces && sessionPath) {
				const spaceParse = JSON.parse(sessionSpaces);
				setSpaces(spaceParse);
				const pathParse = JSON.parse(sessionPath);
				const currentLocation = pathParse[pathParse.length - 1];

				if (["search", "trash", "recent"].includes(currentLocation.id)) {
					switchSpace(currentLocation);
				} else {
					setPath(pathParse);
					getFileList(currentLocation.id);
				}
			} else {
				getSpaces();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLogin]);

	useEffect(() => {
		if (isLogin) {
			service.get("/user/profile").then((res) => {
				localStorage.setItem('user_info', JSON.stringify(res))
			}).catch(err => console.log(err));
		}
	}, [isLogin]);

	useEffect(() => {
		const historyView = localStorage.getItem("view") || "list";
		setView(historyView);
	}, []);

	useEffect(() => {
		setSelectedItems({});
	}, [path]);

	useEffect(() => {
		const data = sortItems(fileList);
		setFileList(data);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sortOrder]);

	useEffect(() => {
		if (path?.length === 0) return;
		sessionStorage.setItem("path", JSON.stringify(path));
	}, [path]);

	const isExplorer = path?.[0]?.id === "explorer";
	const isLibraryView = activeTab === 'docs' && path?.[0]?.id === 'library';

	const tabs = [
		{ id: 'docs', label: 'Document', icon: faFileLines },
		{ id: 'user-management', label: 'Users', icon: faUsersLine },
		{ id: 'mail-group', label: 'Mail Group', icon: faUserGroup },
		{ id: 'timeline', label: 'Calendar', icon: faCalendarDays },
		// { id: 'contract-reviewer', label: 'Contracts', icon: faFileContract },
		{ id: 'crm', label: 'CRM', icon: faBriefcase }
	];

	const handleTabChange = (tab) => {
		const hostname = window.location.hostname;
		switch (tab) {
			case 'user-management':
				window.location.href = `https://${hostname}/users`;
				return;
			case 'docs':
				window.location.href = `https://${hostname}/document`;
				return;
			case 'library':
				window.location.href = `https://${hostname}/library`;
				return;
			case 'mail-group':
				window.location.href = `https://${hostname}/mailgroup`;
				return;
			case 'timeline':
				window.location.href = `https://${hostname}/timeline`;
				return;
			// case 'contract-reviewer':
			// 	window.location.href = `https://${hostname}/contract`;
			// 	return;
			case 'crm':
				window.location.href = `https://${hostname}/crm`;
				return;
			default:
				break;
		}
		setActiveTab(tab);
		if (tab === 'my-profile' && !selectedChildrenTab) {
			setSelectedChildrenTab('profile');
		}
	}

	const handleChildrenTabChange = (childTab) => {
		setSelectedChildrenTab(childTab);
	}

	if (!isLogin && !checkingAuth) {
		if (window.location.pathname !== '/login') {
			window.location.replace('/login?redirect=' + encodeURIComponent(window.location.pathname + window.location.search));
			return null;
		}
		return <LoginForm doLogin={login} />;
	}

	return (
		<div className={`relative h-screen overflow-visible`}>
			<div className="fixed top-0 left-0 right-0 z-[1000] bg-gray-100 shadow-md">
				<Tabs
					logout={logout}
					onChange={handleTabChange}
					onChangeChildren={handleChildrenTabChange}
				>
					{tabs.map((tab) => (
						<Tab key={tab.id} id={tab.id} label={tab.label} icon={tab.icon} children={tab.children} onChange={handleTabChange}
							onChangeChildren={handleChildrenTabChange} />
					))}
				</Tabs>
			</div>
			<div className="pt-12 sm:pt-9 flex App">
				{activeTab === 'docs' &&
					<>
						<Sidebar
							spaces={spaces}
							path={path}
							switchSpace={switchSpace}
							setSpaces={setSpaces}
							spaceInfo={spaceInfo}
						/>
						<div className="hidden w-56 sm:block"></div>
					</>
				}
				{
					<div
						className="flex-1 h-screen px-2 sm:px-4 pb-4 min-w-[300px] sm:min-w-[600px] sticky-top"
						onDrop={handleFileDrop}
						onDragOver={(e) => e.preventDefault()}
						onDragEnter={(e) => e.preventDefault()}
					>
						{
							((activeTab === "docs" || activeTab === "trash") && !isLibraryView) && <Toolbar
								spaces={spaces}
								path={path}
								setPath={setPath}
								fileList={fileList}
								setFileList={setFileList}
								spaceInfo={spaceInfo}
								getFileList={getFileList}
								selectedItems={selectedItems}
								setSelectedItems={setSelectedItems}
								openFolder={openFolder}
								sortOrder={sortOrder}
								setSortOrder={setSortOrder}
								upload={upload}
								view={view}
								setView={setView}
								checkExists={checkExists}
								resolvedPermissions={resolvedPermissions}
								extensions={extensions}
								setIsPropertiesModalVisible={setIsPropertiesModalVisible}
								setCurrentItem={setCurrentItem}
								isTrashTab={activeTab === "trash"}
								switchSpace={switchSpace}
								uploadFolder={uploadFolderAction}
							/>
						}

						<div
							className={` pb-4 box-border pl-[2px] pr-[2px] ${isLibraryView ? "w-full" : view === "grid" ? "flex flex-wrap gap-2 sm:gap-5" : "space-y-4"
								}`}
						>
							{!loading && !isExplorer && (
								<div className={`border-1 border-white rounded gap-4 w-full`}>
									{((activeTab === 'docs' || activeTab === 'trash') && !isLibraryView) && <div
										className={` pb-4 box-border pl-[2px] pr-[2px] pt-[160px] sm:pt-[100px] ${view === "grid" ? "flex flex-wrap gap-2 sm:gap-5" : "space-y-4"
											}`}
									>
										<FileList
											openFolder={openFolder}
											fileList={fileList}
											setFileList={setFileList}
											extensions={extensions}
											handleItemClick={handleItemClick}
											spaceInfo={spaceInfo}
											getFileList={getFileList}
											setPath={setPath}
											setIsPropertiesModalVisible={setIsPropertiesModalVisible}
											setCurrentItem={setCurrentItem}
											view={view}
											selectedItems={selectedItems}
											setSelectedItems={setSelectedItems}
										/>
									</div>}
									{isLibraryView && (
										<div className="pb-4 box-border pl-[2px] pr-[2px] pt-1 w-full">
											<LibraryPage />
										</div>
									)}
									{(activeTab === 'my-profile') && <div
										className={` pb-4 box-border pl-[2px] pr-[2px]`}
									>
										<Proflie
											handleLogout={logout}
											selectedChildrenTab={selectedChildrenTab}
											setSelectedChildrenTab={setSelectedChildrenTab}
										/>
									</div>}
									{(activeTab === 'mail-group') && <div
										className={` pb-4 box-border pl-[2px] pr-[2px]`}
									>
										<MailGroupManagement />
									</div>}
									{(activeTab === 'user-management') && <div
										className={` pb-4 box-border pl-[2px] pr-[2px]`}>
									</div>}
									{(activeTab === 'timeline') && <div
										className={` pb-4 box-border pl-[2px] pr-[2px]`}>
									</div>}
									{(activeTab === 'contract-reviewer') && <div
										className={` pb-4 box-border pl-[2px] pr-[2px]`}>
									</div>}
								</div>
							)}
							{!loading &&
								path.length > 0 &&
								fileList?.length === 0 &&
								!isExplorer && activeTab === 'docs' && !isLibraryView && (
									<div className="flex items-center text-zinc-700">
										There are no files in the current directory.
									</div>
								)}
							{isExplorer && (
								<div className="w-full">
									<FileList
										openFolder={openFolder}
										fileList={fileList}
										setFileList={setFileList}
										extensions={extensions}
										handleItemClick={handleItemClick}
										spaceInfo={spaceInfo}
										getFileList={getFileList}
										setPath={setPath}
										setIsPropertiesModalVisible={setIsPropertiesModalVisible}
										setCurrentItem={setCurrentItem}
										view={view}
										selectedItems={selectedItems}
									/>
								</div>
							)}
						</div>
					</div>
				}
				<Progress uploadList={uploadList} setUploadList={setUploadList} />
				{isExistModalVisible && (
					<Dialog
						title={() => (
							<div>
								The <span className="text-zinc-800">{currentFiles[0]?.name}</span>{" "}
								already exists. Do you want to replace it?
							</div>
						)}
						confirmButtonColor="bg-[#ffa270]"
						onConfirm={() => replaceFile(true)}
						onCancel={continueUpload}
						onCloseModal={() => {
							setIsExistModalVisible(false);
							setCurrentFiles([]);
						}}
						confirmText="Replace it"
						cancelText="No, just upload"
					/>
				)}
				{isPropertiesModalVisible && (
					<PropertiesModal
						setCurrentItem={setCurrentItem}
						getFileList={getFileList}
						spaceInfo={spaceInfo}
						setIsPropertiesModalVisible={setIsPropertiesModalVisible}
						itemInfo={currentItem}
						permissions={resolvedPermissions}
					/>
				)}
				{activeSection === 'search' && (
					<div className="flex-1 h-screen px-2 sm:px-4 pb-4 min-w-[300px] sm:min-w-[600px]">
						<Search
							onItemClick={(item) => {
								if (!item.is_folder) {
									// Handle file click
									setCurrentItem(item);
								}
							}}
							spaceInfo={spaceInfo}
							getFileList={getFileList}
							openFolder={openFolder}
							setPath={setPath}
							setIsPropertiesModalVisible={setIsPropertiesModalVisible}
							setCurrentItem={setCurrentItem}
							view={view}
							setView={setView}
						/>
					</div>
				)}
			</div>
		</div>
	);
}

export default App;
